import { useState, useEffect, useRef } from 'react';
import LauncherButton from './LauncherButton';
import ChatHeader from './ChatHeader';
import TypingIndicator from './TypingIndicator';
import MessageBubble from './MessageBubble';
import SuggestionChips from './SuggestionChips';
import ChatInput from './ChatInput';
import { useChatSession } from './useChatSession';

// Preserved verbatim behind the prowl.v2.customer flag (task requirement 9):
// the pre-rewire two-call faq/ai-search + conciergeSearch widget, byte-for-byte,
// so flag-off traffic is unaffected by the Prowl v2 rewire in ProwlChatWidgetV2.
const ProwlChatWidgetLegacy = () => {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { messages, isLoading, suggestions, send, reset, setFeedback } =
    useChatSession();

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSuggestionClick = (label: string) => {
    send(label);
  };

  return (
    <>
      {/* Launcher (collapsed state) */}
      {!isOpen && <LauncherButton onClick={() => setIsOpen(true)} />}

      {/* Panel (open state) */}
      {isOpen && (
        <div
          className="fixed right-7 bottom-7 z-50 flex flex-col bg-white rounded-[20px] border border-gray-200 overflow-hidden"
          style={{
            width: '392px',
            maxWidth: 'calc(100vw - 32px)',
            height: '600px',
            maxHeight: 'calc(100vh - 56px)',
            boxShadow: '0 24px 64px rgba(15,23,42,0.24)',
            animation: 'hp-in 0.22s ease',
          }}
        >
          {/* Gradient accent bar */}
          <div
            className="h-1 shrink-0"
            style={{
              background:
                'linear-gradient(90deg, var(--chat-accent, #317690), var(--brand-navy, #163544))',
            }}
          />

          <ChatHeader onReset={reset} onClose={() => setIsOpen(false)} />

          {/* Messages area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-[18px] flex flex-col gap-3 bg-gray-50"
          >
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onFeedback={(value) => setFeedback(msg.id, value)}
              />
            ))}

            {/* Typing indicator */}
            {isLoading && <TypingIndicator />}

            {/* Suggestion chips */}
            {!isLoading && suggestions.length > 0 && (
              <SuggestionChips
                suggestions={suggestions}
                onSelect={handleSuggestionClick}
              />
            )}
          </div>

          <ChatInput onSend={send} disabled={isLoading} />
        </div>
      )}
    </>
  );
};

export default ProwlChatWidgetLegacy;
